const { qs, qsa } = require('../../libs/qs')
const audioBufferToWav = require('audiobuffer-to-wav')
const { faderH } = require('../common/ctrlz.js')

const paintCanvas = qs('#paintcanvas')
const waveCanvas = qs('#wavecanvas')
const paintCtx = paintCanvas.getContext('2d', { willReadFrequently: true })
const waveCtx = waveCanvas.getContext('2d')

function getThemeColors() {
  // Try to get theme from localStorage first (updated by theme editor)
  let currentTheme
  try {
    const stored = localStorage.getItem('currentTheme')
    if (stored) {
      currentTheme = JSON.parse(stored)
    }
  } catch (e) {}
  
  // Fallback to server-side injected theme
  if (!currentTheme) {
    currentTheme = window.Theme
  }
  
  const themeColors = Object.values(currentTheme.data)
  const themeRgb = themeColors.map((el) => {
    return { r: parseInt(el.slice(1, 3), 16), g: parseInt(el.slice(3, 5), 16), b: parseInt(el.slice(5, 7), 16) }
  })
  return { themeColors, themeRgb, currentTheme }
}

let { themeColors, themeRgb, currentTheme } = getThemeColors()

window.CONTROLZ = {}
window.themeColors = themeColors
window.currentTheme = currentTheme
window.themeRgb = themeRgb

const state = {
  pointerDown: false,
  brushSize: 18,
  brushGain: 0.65,
  softness: 0.35,
  sprayDensity: 12,
  brushMode: 'paint',
  brushShape: 'round',
  renderSeconds: 6,
  sampleRate: 22050,
  harmonics: 24,
  fxForce: 0.35,
  fxAmount: 18,
  history: [],
  future: [],
  lastPoint: null,
  audioBuffer: null,
  audioUrl: '',
  audioContext: null,
  sourceNode: null,
  playbackStartTime: 0,
  playheadFrame: null,
  loopEnabled: false,
  needsRerender: false,
}

const statusline = qs('#statusline')
const fileInput = qs('#fileinput')
const playheadEl = qs('#playhead')
const controls = {
  brushSize: qs('#brushsize'),
  brushGain: qs('#brushgain'),
  softness: qs('#softness'),
  sprayDensity: qs('#spraydensity'),
  renderSeconds: qs('#duration'),
  harmonics: qs('#harmonics'),
  fxForce: qs('#fxforce'),
  fxAmount: qs('#fxamount'),
  sampleRate: qs('#samplerate'),
  brushMode: qs('#brushmode'),
  brushShape: qs('#brushshape'),
  loopAudio: qs('#loopaudio'),
}
const paintCanvasSize = 1024
let theme = {
  bg: window.themeColors[0],
  panel: window.themeColors[1] ,
  accent: window.themeColors[8] ,
  bright: window.themeColors[window.themeColors.length - 1],
  mid: window.themeColors[Math.floor(window.themeColors.length / 2)],
  low: window.themeColors[3] ,
}
let monoColor = themeRgb[themeRgb.length - 1] || { r: 255, g: 255, b: 255 }

paintCanvas.width = paintCanvasSize
paintCanvas.height = paintCanvasSize

function setStatus(message) {
  statusline.textContent = message
}

function initializeFaders() {
  Object.values(controls).forEach((input) => {
    if (input && input.type === 'range') {
      faderH(input)
    }
  })
}

function getControlValue(key, fallback, parser = Number) {
  const input = controls[key]
  if (!input) {
    return fallback
  }
  const parsed = parser(input.value)
  return Number.isNaN(parsed) ? fallback : parsed
}

function syncStateFromControls() {
  state.brushSize = getControlValue('brushSize', state.brushSize)
  state.brushGain = getControlValue('brushGain', state.brushGain)
  state.softness = getControlValue('softness', state.softness)
  state.sprayDensity = getControlValue('sprayDensity', state.sprayDensity)
  state.renderSeconds = getControlValue('renderSeconds', state.renderSeconds)
  state.harmonics = getControlValue('harmonics', state.harmonics)
  state.fxForce = getControlValue('fxForce', state.fxForce)
  state.fxAmount = getControlValue('fxAmount', state.fxAmount)
  state.sampleRate = controls.sampleRate ? Number(controls.sampleRate.value) : state.sampleRate
  state.brushMode = controls.brushMode ? controls.brushMode.value : state.brushMode
  state.brushShape = controls.brushShape ? controls.brushShape.value : state.brushShape
  state.loopEnabled = controls.loopAudio ? controls.loopAudio.classList.contains('active') : state.loopEnabled
}

function pushHistory() {
  state.history.push(paintCtx.getImageData(0, 0, paintCanvas.width, paintCanvas.height))
  if (state.history.length > 30) {
    state.history.shift()
  }
  state.future = []
}

function restoreImageData(imageData) {
  paintCtx.putImageData(imageData, 0, 0)
}

function fillBackground() {
  paintCtx.fillStyle = window.themeColors[0]
  paintCtx.fillRect(0, 0, paintCanvas.width, paintCanvas.height)
}

function clearCanvas() {
  pushHistory()
  fillBackground()
  setStatus('Canvas cleared.')
  updateStats()
}

function canvasPoint(event) {
  const rect = paintCanvas.getBoundingClientRect()
  return {
    x: ((event.clientX - rect.left) / rect.width) * paintCanvas.width,
    y: ((event.clientY - rect.top) / rect.height) * paintCanvas.height,
  }
}

function getPixelLuminance(data, index) {
  return Math.max(0, Math.min(255, Math.round(
    data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114
  )))
}

function applyThemeMonochrome(data, index, intensity) {
  const ratio = Math.max(0, Math.min(1, intensity / 255))
  data[index] = Math.round(monoColor.r * ratio)
  data[index + 1] = Math.round(monoColor.g * ratio)
  data[index + 2] = Math.round(monoColor.b * ratio)
  data[index + 3] = intensity > 0 ? 255 : 255
}

function intensityFromMode(base) {
  if (state.brushMode === 'erase') {
    return Math.max(0, base - state.brushGain * 255)
  }
  return Math.min(255, base + state.brushGain * 255)
}

function shadePixel(data, index, amount) {
  const current = getPixelLuminance(data, index)
  const target = intensityFromMode(current)
  const mixed = Math.max(0, Math.min(255, current + (target - current) * amount))
  applyThemeMonochrome(data, index, mixed)
}

function normalizeImageDataToThemeMonochrome(imageData) {
  const { data } = imageData
  for (let i = 0; i < data.length; i += 4) {
    const luminance = getPixelLuminance(data, i)
    applyThemeMonochrome(data, i, luminance)
  }
  return imageData
}

function normalizeCanvasToThemeMonochrome() {
  const imageData = paintCtx.getImageData(0, 0, paintCanvas.width, paintCanvas.height)
  normalizeImageDataToThemeMonochrome(imageData)
  paintCtx.putImageData(imageData, 0, 0)
}

function paintAt(point) {
  const radius = state.brushSize / 2
  const left = Math.max(0, Math.floor(point.x - radius - 2))
  const top = Math.max(0, Math.floor(point.y - radius - 2))
  const size = Math.min(paintCanvas.width - left, Math.ceil(radius * 2 + 4))
  const height = Math.min(paintCanvas.height - top, Math.ceil(radius * 2 + 4))
  const imageData = paintCtx.getImageData(left, top, size, height)
  const { data, width } = imageData

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const dx = x + left - point.x
      const dy = y + top - point.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      let amount = 0

      if (state.brushShape === 'square') {
        amount = Math.max(Math.abs(dx), Math.abs(dy)) <= radius ? 1 : 0
      } else if (state.brushShape === 'harmonic') {
        const harmonicDistance = Math.abs(dx)
        const band = Math.abs((dy % Math.max(radius * 0.7, 1)))
        amount = harmonicDistance <= radius && band < radius * 0.18 ? 1 - harmonicDistance / Math.max(radius, 1) : 0
      } else if (state.brushShape === 'soft') {
        amount = distance <= radius ? Math.pow(1 - distance / Math.max(radius, 1), 2 + state.softness * 4) : 0
      } else {
        amount = distance <= radius ? 1 - distance / Math.max(radius, 1) : 0
      }

      if (amount <= 0) {
        continue
      }

      const index = (y * width + x) * 4
      shadePixel(data, index, amount)
    }
  }

  paintCtx.putImageData(imageData, left, top)
}

function sprayAt(point) {
  for (let i = 0; i < state.sprayDensity; i += 1) {
    const angle = Math.random() * Math.PI * 2
    const radius = Math.random() * state.brushSize
    paintAt({
      x: point.x + Math.cos(angle) * radius,
      y: point.y + Math.sin(angle) * radius,
    })
  }
}

function stampAt(point) {
  const offset = state.brushSize * 0.55
  paintAt(point)
  paintAt({ x: point.x - offset, y: point.y })
  paintAt({ x: point.x + offset, y: point.y })
  paintAt({ x: point.x, y: point.y - offset })
  paintAt({ x: point.x, y: point.y + offset })
}

function drawLine(from, to) {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const distance = Math.max(Math.abs(dx), Math.abs(dy), 1)
  for (let step = 0; step <= distance; step += 1) {
    const point = {
      x: from.x + (dx * step) / distance,
      y: from.y + (dy * step) / distance,
    }
    applyBrush(point)
  }
}

function applyBrush(point) {
  syncStateFromControls()
  if (state.brushMode === 'spray') {
    sprayAt(point)
    return
  }
  if (state.brushMode === 'stamps') {
    stampAt(point)
    return
  }
  paintAt(point)
}

function beginPaint(event) {
  event.preventDefault()
  pushHistory()
  state.pointerDown = true
  state.lastPoint = canvasPoint(event)
  applyBrush(state.lastPoint)
  updateStats()
  
  // If audio is playing in loop mode, mark for rerender at end of loop
  if (state.sourceNode && state.loopEnabled) {
    state.needsRerender = true
  }
}

function movePaint(event) {
  if (!state.pointerDown) {
    return
  }
  const point = canvasPoint(event)
  if (state.brushMode === 'line') {
    drawLine(state.lastPoint, point)
  } else {
    applyBrush(point)
  }
  state.lastPoint = point
  updateStats()
  
  // If audio is playing in loop mode, mark for rerender at end of loop
  if (state.sourceNode && state.loopEnabled) {
    state.needsRerender = true
  }
}

function endPaint() {
  state.pointerDown = false
  state.lastPoint = null
}

function updateStats() {
  const imageData = paintCtx.getImageData(0, 0, paintCanvas.width, paintCanvas.height)
  const { data } = imageData
  let peaks = 0
  for (let i = 0; i < data.length; i += 4) {
    if (getPixelLuminance(data, i) > 42) {
      peaks += 1
    }
  }
  qs('#framecount').textContent = String(paintCanvas.width)
  qs('#peakcount').textContent = String(peaks)
  qs('#durationvalue').textContent = `${state.renderSeconds.toFixed(1)} s`
  qs('#sampleratevalue').textContent = String(state.sampleRate)
}

function rebuildAudioContext() {
  if (state.audioContext) {
    stopPreview()
    state.audioContext.close().catch(() => {})
    state.audioContext = null
  }
}

function ensureAudioContext() {
  if (!state.audioContext) {
    state.audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: state.sampleRate })
  }
  return state.audioContext
}

function resetPlayhead() {
  if (!playheadEl) {
    return
  }
  playheadEl.classList.remove('active')
  playheadEl.style.transform = 'translateX(0px)'
}

function updatePlayhead() {
  if (!state.sourceNode || !state.audioBuffer || !playheadEl || !state.audioContext) {
    state.playheadFrame = null
    return
  }
  const elapsed = Math.max(0, state.audioContext.currentTime - state.playbackStartTime)
  const duration = Math.max(state.audioBuffer.duration, 0.001)
  const normalized = state.loopEnabled ? (elapsed % duration) / duration : Math.min(elapsed / duration, 1)
  const frameWidth = paintCanvas.getBoundingClientRect().width
  playheadEl.style.transform = `translateX(${normalized * frameWidth}px)`
  state.playheadFrame = requestAnimationFrame(updatePlayhead)
}

function startPlayhead() {
  if (!playheadEl) {
    return
  }
  if (state.playheadFrame) {
    cancelAnimationFrame(state.playheadFrame)
  }
  playheadEl.classList.add('active')
  state.playheadFrame = requestAnimationFrame(updatePlayhead)
}

function stopPreview() {
  if (state.playheadFrame) {
    cancelAnimationFrame(state.playheadFrame)
    state.playheadFrame = null
  }
  resetPlayhead()
  if (state.sourceNode) {
    state.sourceNode.onended = null
    state.sourceNode.stop()
    state.sourceNode.disconnect()
    state.sourceNode = null
  }
}

function renderWavePreview(samples) {
  waveCtx.clearRect(0, 0, waveCanvas.width, waveCanvas.height)
  waveCtx.fillStyle = window.themeColors[0] 
  waveCtx.fillRect(0, 0, waveCanvas.width, waveCanvas.height)

  const gradient = waveCtx.createLinearGradient(0, 0, waveCanvas.width, 0)
  gradient.addColorStop(0, window.themeColors[3])
  gradient.addColorStop(0.5, window.themeColors[Math.floor(window.themeColors.length / 2)])
  gradient.addColorStop(1, window.themeColors[8])
  waveCtx.strokeStyle = gradient
  waveCtx.lineWidth = 2
  waveCtx.beginPath()

  const step = Math.max(1, Math.floor(samples.length / waveCanvas.width))
  const mid = waveCanvas.height / 2

  for (let x = 0; x < waveCanvas.width; x += 1) {
    const sample = samples[Math.min(samples.length - 1, x * step)] || 0
    const y = mid - sample * (waveCanvas.height * 0.42)
    if (x === 0) {
      waveCtx.moveTo(x, y)
    } else {
      waveCtx.lineTo(x, y)
    }
  }

  waveCtx.stroke()
}

function renderAudio() {
  syncStateFromControls()
  setStatus('Rendering audio from paint data...')
  const imageData = paintCtx.getImageData(0, 0, paintCanvas.width, paintCanvas.height)
  const { data, width, height } = imageData
  const audioContext = ensureAudioContext()
  const sampleRate = audioContext.sampleRate
  const totalSamples = Math.floor(sampleRate * state.renderSeconds)
  const buffer = audioContext.createBuffer(1, totalSamples, sampleRate)
  const channel = buffer.getChannelData(0)
  const harmonics = state.harmonics

  for (let i = 0; i < totalSamples; i += 1) {
    const timeNorm = i / Math.max(totalSamples - 1, 1)
    const x = Math.min(width - 1, Math.floor(timeNorm * width))
    let sample = 0

    for (let band = 0; band < harmonics; band += 1) {
      const y = Math.floor((band / Math.max(harmonics - 1, 1)) * (height - 1))
      const flippedY = height - 1 - y
      const index = (flippedY * width + x) * 4
      const energy = Math.max(0, getPixelLuminance(data, index) - 18) / 237
      if (energy <= 0.01) {
        continue
      }
      const freq = 45 * Math.pow(1.185, band)
      sample += Math.sin((2 * Math.PI * freq * i) / sampleRate) * energy
    }

    channel[i] = Math.max(-1, Math.min(1, sample / Math.max(4, harmonics * 0.22)))
  }

  state.audioBuffer = buffer
  stopPreview()
  if (state.audioUrl) {
    URL.revokeObjectURL(state.audioUrl)
  }
  const wav = audioBufferToWav(buffer)
  const blob = new Blob([wav], { type: 'audio/wav' })
  state.audioUrl = URL.createObjectURL(blob)
  renderWavePreview(channel)
  updateStats()
  setStatus('Render complete. Press play or save the WAV file.')
}

async function playPreview() {
  if (!state.audioBuffer) {
    renderAudio()
  }

  const audioContext = ensureAudioContext()
  await audioContext.resume()
  stopPreview()

  renderAudio()

  const source = audioContext.createBufferSource()
  source.buffer = state.audioBuffer
  source.loop = false // Use custom loop for rerender support
  source.connect(audioContext.destination)
  source.onended = () => {
    if (state.sourceNode === source) {
      if (state.loopEnabled) {
        // Check if we need to rerender with updated canvas
        if (state.needsRerender) {
          state.needsRerender = false
          renderAudio()
          const newSource = audioContext.createBufferSource()
          newSource.buffer = state.audioBuffer
          newSource.loop = false
          newSource.connect(audioContext.destination)
          newSource.onended = () => {
            if (state.sourceNode === newSource) {
              state.sourceNode = null
              resetPlayhead()
              setStatus('Preview finished.')
            }
          }
          state.sourceNode = newSource
          state.playbackStartTime = audioContext.currentTime
          newSource.start()
          startPlayhead()
          setStatus('Playing preview in loop (updated).')
        } else {
          // Just restart with same buffer
          const newSource = audioContext.createBufferSource()
          newSource.buffer = state.audioBuffer
          newSource.loop = false
          newSource.connect(audioContext.destination)
          newSource.onended = () => {
            if (state.sourceNode === newSource) {
              state.sourceNode = null
              resetPlayhead()
              setStatus('Preview finished.')
            }
          }
          state.sourceNode = newSource
          state.playbackStartTime = audioContext.currentTime
          newSource.start()
          startPlayhead()
          setStatus('Playing preview in loop.')
        }
      } else {
        state.sourceNode = null
        resetPlayhead()
        setStatus('Preview finished.')
      }
    }
  }
  state.sourceNode = source
  state.playbackStartTime = audioContext.currentTime
  source.start()
  startPlayhead()
  setStatus(state.loopEnabled ? 'Playing preview in loop.' : 'Playing preview through Web Audio.')
}

function undo() {
  if (!state.history.length) {
    return
  }
  state.future.push(paintCtx.getImageData(0, 0, paintCanvas.width, paintCanvas.height))
  restoreImageData(state.history.pop())
  setStatus('Undo applied.')
  updateStats()
}

function redo() {
  if (!state.future.length) {
    return
  }
  state.history.push(paintCtx.getImageData(0, 0, paintCanvas.width, paintCanvas.height))
  restoreImageData(state.future.pop())
  setStatus('Redo applied.')
  updateStats()
}

function mutatePixels(mutator) {
  pushHistory()
  const imageData = paintCtx.getImageData(0, 0, paintCanvas.width, paintCanvas.height)
  mutator(imageData)
  normalizeImageDataToThemeMonochrome(imageData)
  paintCtx.putImageData(imageData, 0, 0)
  updateStats()
}

function addNoise(kind) {
  syncStateFromControls()
  mutatePixels((imageData) => {
    const { data, width, height } = imageData
    for (let i = 0; i < data.length; i += 4) {
      const pixel = i / 4
      const x = pixel % width
      const y = Math.floor(pixel / width)
      let value = 0
      if (kind === 'white') {
        value = Math.random() < state.fxForce ? Math.random() * 255 : 0
      } else if (kind === 'pink') {
        value = Math.random() < state.fxForce * 0.7 ? (Math.random() + Math.random()) * 90 : 0
      } else if (kind === 'velvet') {
        value = (x + y + Math.random() * state.fxAmount) % Math.max(2, Math.floor(22 - state.fxAmount / 8)) === 0 ? 255 : 0
      } else if (kind === 'bands') {
        value = y % Math.max(2, Math.floor(state.fxAmount)) < Math.max(1, state.fxAmount * 0.12) ? 180 + Math.random() * 70 : 0
      } else if (kind === 'beats') {
        const spacing = Math.max(6, Math.floor(state.fxAmount * 1.5))
        const widthPulse = Math.max(1, Math.floor(spacing * Math.max(0.08, state.fxForce * 0.45)))
        const pulse = x % spacing < widthPulse
        const env = 0.35 + 0.65 * Math.sin((Math.PI * y) / Math.max(1, height - 1))
        value = pulse ? 180 + env * 75 : 0
      } else if (kind === 'combs') {
        const spacing = Math.max(5, Math.floor(state.fxAmount))
        const vertical = x % spacing < Math.max(1, Math.floor(spacing * 0.2)) ? 220 : 0
        const horizontal = y % Math.max(8, Math.floor(state.fxAmount * 1.4)) < 1 ? 110 : 0
        value = Math.min(255, vertical + horizontal)
      } else if (kind === 'lowpass') {
        const cutoff = 1 - Math.min(1, y / Math.max(1, height - 1))
        value = Math.pow(cutoff, 0.6 + state.fxForce * 2.4) * 255
      } else if (kind === 'highpass') {
        const lift = Math.min(1, y / Math.max(1, height - 1))
        value = Math.pow(lift, 0.6 + state.fxForce * 2.4) * 255
      } else if (kind === 'bandpass') {
        const center = 0.2 + ((state.fxAmount % 100) / 100) * 0.6
        const yn = y / Math.max(1, height - 1)
        const dist = Math.abs(yn - center)
        const widthBand = Math.max(0.04, 0.22 - state.fxForce * 0.16)
        value = Math.max(0, 1 - dist / widthBand) * 255
      } else if (kind === 'notch') {
        const center = 0.2 + ((state.fxAmount % 100) / 100) * 0.6
        const yn = y / Math.max(1, height - 1)
        const dist = Math.abs(yn - center)
        const widthBand = Math.max(0.03, 0.18 - state.fxForce * 0.12)
        value = (dist > widthBand ? 1 : dist / widthBand) * 255
      } else if (kind === 'up') {
        const xn = x / Math.max(1, width - 1)
        const yn = 1 - y / Math.max(1, height - 1)
        value = Math.pow(Math.max(0, xn * yn), 0.75) * 255
      } else if (kind === 'down') {
        const xn = 1 - x / Math.max(1, width - 1)
        const yn = y / Math.max(1, height - 1)
        value = Math.pow(Math.max(0, xn * yn), 0.75) * 255
      }
      const mixed = Math.max(0, Math.min(255, value))
      data[i] = Math.max(data[i], mixed)
      data[i + 1] = Math.max(data[i + 1], mixed * 0.7)
      data[i + 2] = Math.max(data[i + 2], mixed * 0.95)
      data[i + 3] = 255
    }
  })
  setStatus(`${kind} noise added.`)
}

function applyFx(type) {
  syncStateFromControls()
  mutatePixels((imageData) => {
    const { data, width, height } = imageData
    if (type === 'invert') {
      for (let i = 0; i < data.length; i += 4) {
        data[i] = 255 - data[i]
        data[i + 1] = 255 - data[i + 1]
        data[i + 2] = 255 - data[i + 2]
      }
      return
    }

    if (type === 'mirror' || type === 'mirrorx') {
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width / 2; x += 1) {
          const left = (y * width + x) * 4
          const right = (y * width + (width - 1 - x)) * 4
          for (let c = 0; c < 4; c += 1) {
            data[right + c] = data[left + c]
          }
        }
      }
      return
    }

    if (type === 'mirrory') {
      for (let y = 0; y < height / 2; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const top = (y * width + x) * 4
          const bottom = ((height - 1 - y) * width + x) * 4
          for (let c = 0; c < 4; c += 1) {
            data[bottom + c] = data[top + c]
          }
        }
      }
      return
    }

    if (type === 'mirrorquad') {
      const copy = new Uint8ClampedArray(data)
      const halfW = Math.ceil(width / 2)
      const halfH = Math.ceil(height / 2)
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const srcX = x < halfW ? x : width - 1 - x
          const srcY = y < halfH ? y : height - 1 - y
          const src = (srcY * width + srcX) * 4
          const dst = (y * width + x) * 4
          data[dst] = copy[src]
          data[dst + 1] = copy[src + 1]
          data[dst + 2] = copy[src + 2]
          data[dst + 3] = 255
        }
      }
      return
    }

    if (type === 'rgbshift') {
      const shifted = new Uint8ClampedArray(data)
      const shift = Math.max(1, Math.floor(state.fxAmount))
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const i = (y * width + x) * 4
          const r = (y * width + Math.min(width - 1, x + shift)) * 4
          const b = (y * width + Math.max(0, x - shift)) * 4
          data[i] = shifted[r]
          data[i + 2] = shifted[b + 2]
        }
      }
      return
    }

    if (type === 'scanlines') {
      const interval = Math.max(2, Math.floor(state.fxAmount / 2))
      for (let y = 0; y < height; y += 1) {
        if (y % interval === 0) {
          for (let x = 0; x < width; x += 1) {
            const i = (y * width + x) * 4
            data[i] *= 1 - state.fxForce * 0.75
            data[i + 1] *= 1 - state.fxForce * 0.75
            data[i + 2] *= 1 - state.fxForce * 0.75
          }
        }
      }
      return
    }

    if (type === 'posterize') {
      const levels = Math.max(2, Math.floor(10 - state.fxForce * 7))
      const step = 255 / levels
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.round(data[i] / step) * step
        data[i + 1] = Math.round(data[i + 1] / step) * step
        data[i + 2] = Math.round(data[i + 2] / step) * step
      }
      return
    }

    if (type === 'threshold') {
      const threshold = Math.floor(255 * (1 - state.fxForce * 0.9))
      for (let i = 0; i < data.length; i += 4) {
        const value = data[i] > threshold ? 255 : 0
        data[i] = value
        data[i + 1] = value
        data[i + 2] = value
      }
      return
    }

    if (type === 'latglitch') {
      const shifted = new Uint8ClampedArray(data)
      const amplitude = Math.max(2, Math.floor(state.fxAmount))
      for (let y = 0; y < height; y += 1) {
        const offset = Math.floor(Math.sin(y / Math.max(4, amplitude * 0.5)) * amplitude * state.fxForce * 4)
        for (let x = 0; x < width; x += 1) {
          const srcX = Math.max(0, Math.min(width - 1, x + offset))
          const src = (y * width + srcX) * 4
          const dst = (y * width + x) * 4
          data[dst] = shifted[src]
          data[dst + 1] = shifted[src + 1]
          data[dst + 2] = shifted[src + 2]
          data[dst + 3] = 255
        }
      }
      return
    }

    if (type === 'blocks') {
      const size = Math.max(4, Math.floor(state.fxAmount))
      for (let i = 0; i < 50 * Math.max(1, Math.floor(state.fxForce * 10)); i += 1) {
        const sx = Math.floor(Math.random() * Math.max(1, width - size))
        const sy = Math.floor(Math.random() * Math.max(1, height - size))
        const dx = Math.floor(Math.random() * Math.max(1, width - size))
        const dy = Math.floor(Math.random() * Math.max(1, height - size))
        const block = paintCtx.getImageData(sx, sy, size, size)
        paintCtx.putImageData(block, dx, dy)
      }
      return
    }

    if (type === 'flipx' || type === 'flipy' || type === 'flipxy') {
      const copy = new Uint8ClampedArray(data)
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const srcX = type === 'flipx' || type === 'flipxy' ? width - 1 - x : x
          const srcY = type === 'flipy' || type === 'flipxy' ? height - 1 - y : y
          const src = (srcY * width + srcX) * 4
          const dst = (y * width + x) * 4
          data[dst] = copy[src]
          data[dst + 1] = copy[src + 1]
          data[dst + 2] = copy[src + 2]
          data[dst + 3] = 255
        }
      }
      return
    }

    if (type === 'blur' || type === 'sharpen' || type === 'emboss' || type === 'edge') {
      const kernels = {
        blur: [1, 1, 1, 1, 1, 1, 1, 1, 1],
        sharpen: [0, -1, 0, -1, 5, -1, 0, -1, 0],
        emboss: [-2, -1, 0, -1, 1, 1, 0, 1, 2],
        edge: [-1, -1, -1, -1, 8, -1, -1, -1, -1],
      }
      const kernel = kernels[type]
      const divisor = type === 'blur' ? 9 : 1
      const copy = new Uint8ClampedArray(data)
      for (let y = 1; y < height - 1; y += 1) {
        for (let x = 1; x < width - 1; x += 1) {
          let sum = 0
          let idx = 0
          for (let ky = -1; ky <= 1; ky += 1) {
            for (let kx = -1; kx <= 1; kx += 1) {
              const src = ((y + ky) * width + (x + kx)) * 4
              sum += getPixelLuminance(copy, src) * kernel[idx]
              idx += 1
            }
          }
          const dst = (y * width + x) * 4
          const value = Math.max(0, Math.min(255, sum / divisor + (type === 'emboss' ? 128 : 0)))
          data[dst] = value
          data[dst + 1] = value
          data[dst + 2] = value
          data[dst + 3] = 255
        }
      }
      return
    }

    if (type === 'diagonal') {
      const copy = new Uint8ClampedArray(data)
      const amount = Math.max(2, Math.floor(state.fxAmount))
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const srcX = (x + Math.floor(y / Math.max(1, amount / 3))) % width
          const src = (y * width + srcX) * 4
          const dst = (y * width + x) * 4
          data[dst] = copy[src]
          data[dst + 1] = copy[src + 1]
          data[dst + 2] = copy[src + 2]
        }
      }
      return
    }

    if (type === 'stairs') {
      const copy = new Uint8ClampedArray(data)
      const stepSize = Math.max(4, Math.floor(state.fxAmount))
      for (let y = 0; y < height; y += 1) {
        const shift = Math.floor(y / stepSize) * Math.max(1, Math.floor(stepSize * state.fxForce * 2))
        for (let x = 0; x < width; x += 1) {
          const srcX = (x + shift) % width
          const src = (y * width + srcX) * 4
          const dst = (y * width + x) * 4
          data[dst] = copy[src]
          data[dst + 1] = copy[src + 1]
          data[dst + 2] = copy[src + 2]
        }
      }
      return
    }

    if (type === 'crush') {
      const levels = Math.max(2, Math.floor(3 + state.fxForce * 10))
      const step = 255 / levels
      for (let i = 0; i < data.length; i += 4) {
        const lum = getPixelLuminance(data, i)
        const crushed = Math.floor(lum / step) * step
        data[i] = crushed
        data[i + 1] = crushed
        data[i + 2] = crushed
      }
      return
    }

    if (type === 'smear') {
      const copy = new Uint8ClampedArray(data)
      const span = Math.max(2, Math.floor(state.fxAmount))
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const srcX = Math.max(0, x - (x % span))
          const src = (y * width + srcX) * 4
          const dst = (y * width + x) * 4
          data[dst] = copy[src]
          data[dst + 1] = copy[src + 1]
          data[dst + 2] = copy[src + 2]
        }
      }
      return
    }

    if (type === 'radial') {
      const copy = new Uint8ClampedArray(data)
      const cx = width / 2
      const cy = height / 2
      const force = state.fxForce * 0.08
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const dx = x - cx
          const dy = y - cy
          const srcX = Math.max(0, Math.min(width - 1, Math.round(cx + dx * (1 - force))))
          const srcY = Math.max(0, Math.min(height - 1, Math.round(cy + dy * (1 - force))))
          const src = (srcY * width + srcX) * 4
          const dst = (y * width + x) * 4
          data[dst] = copy[src]
          data[dst + 1] = copy[src + 1]
          data[dst + 2] = copy[src + 2]
        }
      }
      return
    }

    if (type === 'slice') {
      const copy = new Uint8ClampedArray(data)
      const sliceWidth = Math.max(4, Math.floor(state.fxAmount))
      for (let x = 0; x < width; x += sliceWidth * 2) {
        const offset = Math.floor((Math.random() - 0.5) * sliceWidth * 6 * Math.max(0.3, state.fxForce))
        for (let y = 0; y < height; y += 1) {
          for (let sx = 0; sx < sliceWidth && x + sx < width; sx += 1) {
            const srcY = Math.max(0, Math.min(height - 1, y + offset))
            const src = (srcY * width + x + sx) * 4
            const dst = (y * width + x + sx) * 4
            data[dst] = copy[src]
            data[dst + 1] = copy[src + 1]
            data[dst + 2] = copy[src + 2]
          }
        }
      }
      return
    }

    if (type === 'kaleido4' || type === 'kaleido8') {
      const copy = new Uint8ClampedArray(data)
      const cx = width / 2
      const cy = height / 2
      const segments = type === 'kaleido8' ? 8 : 4
      const wedge = (Math.PI * 2) / segments
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const dx = x - cx
          const dy = y - cy
          const radius = Math.sqrt(dx * dx + dy * dy)
          let angle = Math.atan2(dy, dx)
          if (angle < 0) angle += Math.PI * 2
          const local = angle % wedge
          const mirrored = local > wedge / 2 ? wedge - local : local
          const srcX = Math.max(0, Math.min(width - 1, Math.round(cx + Math.cos(mirrored) * radius)))
          const srcY = Math.max(0, Math.min(height - 1, Math.round(cy + Math.sin(mirrored) * radius)))
          const src = (srcY * width + srcX) * 4
          const dst = (y * width + x) * 4
          data[dst] = copy[src]
          data[dst + 1] = copy[src + 1]
          data[dst + 2] = copy[src + 2]
          data[dst + 3] = 255
        }
      }
      return
    }

    if (type === 'prismx' || type === 'prismy') {
      const copy = new Uint8ClampedArray(data)
      const cells = Math.max(2, Math.min(8, Math.floor(state.fxForce * 10) + 2))
      const cellW = Math.max(1, Math.floor(width / cells))
      const cellH = Math.max(1, Math.floor(height / cells))
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const localX = x % cellW
          const localY = y % cellH
          const srcX = type === 'prismx' && Math.floor(x / cellW) % 2 === 1 ? cellW - 1 - localX : localX
          const srcY = type === 'prismy' && Math.floor(y / cellH) % 2 === 1 ? cellH - 1 - localY : localY
          const clampedX = Math.max(0, Math.min(width - 1, srcX))
          const clampedY = Math.max(0, Math.min(height - 1, srcY))
          const src = (clampedY * width + clampedX) * 4
          const dst = (y * width + x) * 4
          data[dst] = copy[src]
          data[dst + 1] = copy[src + 1]
          data[dst + 2] = copy[src + 2]
          data[dst + 3] = 255
        }
      }
      return
    }
  })
  setStatus(`${type} effect applied.`)
}

function bakeImageToSpectralData(image) {
  const tempCanvas = document.createElement('canvas')
  tempCanvas.width = paintCanvas.width
  tempCanvas.height = paintCanvas.height
  const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true })
  tempCtx.fillStyle = window.themeColors[0]
  tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)
  tempCtx.drawImage(image, 0, 0, tempCanvas.width, tempCanvas.height)

  const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
  const { data } = imageData
  for (let i = 0; i < data.length; i += 4) {
    const luminance = getPixelLuminance(data, i)
    const shaped = Math.round(Math.pow(luminance / 255, 1.15) * 255)
    applyThemeMonochrome(data, i, shaped)
  }
  return imageData
}

function loadImage(file) {
  const reader = new FileReader()
  reader.onload = () => {
    const image = new Image()
    image.onload = () => {
      pushHistory()
      fillBackground()
      const spectralImage = bakeImageToSpectralData(image)
      paintCtx.putImageData(spectralImage, 0, 0)
      setStatus(`Image loaded: ${file.name}`)
      updateStats()
    }
    image.src = reader.result
  }
  reader.readAsDataURL(file)
}

Object.values(controls).forEach((input) => {
  if (input && input.type === 'range') {
    input.addEventListener('change', syncStateFromControls)
  }
})

controls.brushMode.addEventListener('change', () => {
  syncStateFromControls()
  setStatus(`Brush mode: ${state.brushMode}`)
})

controls.brushShape.addEventListener('change', () => {
  syncStateFromControls()
  setStatus(`Brush shape: ${state.brushShape}`)
})

controls.sampleRate.addEventListener('change', () => {
  syncStateFromControls()
  rebuildAudioContext()
  setStatus(`Sample rate: ${state.sampleRate} Hz`)
})

controls.loopAudio.addEventListener('click', () => {
  controls.loopAudio.classList.toggle('active')
  syncStateFromControls()
  setStatus(state.loopEnabled ? 'Loop mode enabled.' : 'Loop mode disabled.')
})

qs('#clearcanvas').addEventListener('click', clearCanvas)
qs('#playaudio').addEventListener('click', () => {
  playPreview().catch(() => {
    setStatus('Unable to start preview.')
  })
})
qs('#stopaudio').addEventListener('click', () => {
  stopPreview()
  setStatus('Preview stopped.')
})
qs('#undostroke').addEventListener('click', undo)
qs('#redostroke').addEventListener('click', redo)
qs('#loadimage').addEventListener('click', () => fileInput.click())
qs('#downloadwav').addEventListener('click', () => {
  renderAudio()
  const link = document.createElement('a')
  link.href = state.audioUrl
  link.download = 'spectropaint.wav'
  link.click()
})

qsa('.noisebtn').forEach((button) => {
  button.addEventListener('click', () => addNoise(button.dataset.noise))
})

qsa('.fxbtn').forEach((button) => {
  button.addEventListener('click', () => applyFx(button.dataset.fx))
})

fileInput.addEventListener('change', (event) => {
  const file = event.target.files && event.target.files[0]
  if (file) {
    loadImage(file)
  }
})

paintCanvas.addEventListener('pointerdown', beginPaint)
paintCanvas.addEventListener('pointermove', movePaint)
window.addEventListener('pointerup', endPaint)
paintCanvas.addEventListener('pointerleave', endPaint)

// Update theme colors on theme change
window.addEventListener('themeChanged', (e) => {
  setTimeout(() => {
    const newTheme = getThemeColors()
    themeColors = newTheme.themeColors
    themeRgb = newTheme.themeRgb
    currentTheme = newTheme.currentTheme
    window.themeColors = themeColors
    window.themeRgb = themeRgb
    window.currentTheme = currentTheme

    theme = {
      bg: window.themeColors[0],
      panel: window.themeColors[1],
      accent: window.themeColors[8],
      bright: window.themeColors[window.themeColors.length - 1],
      mid: window.themeColors[Math.floor(window.themeColors.length / 2)],
      low: window.themeColors[3],
    }
    monoColor = themeRgb[themeRgb.length - 1] || { r: 255, g: 255, b: 255 }

    fillBackground()
    normalizeCanvasToThemeMonochrome()
  }, 100)
})

fillBackground()
initializeFaders()
syncStateFromControls()
updateStats()
setStatus('Ready to paint.')

// ----------------------------------------------------------------------------
// Bucket integration — export WAV to bucket, drag images from bucket to canvas
// ----------------------------------------------------------------------------
const initLegacyBucket = require('./legacy-bucket-integration.js');

const _spectropaintBucket = initLegacyBucket({
    appName: 'spectropaint',
    onBucketDrop: (e) => {
        // Allow dropping images from the app to the bucket
        const isCanvas = e.dataTransfer.getData('application/x-spectropaint-canvas')
        if (isCanvas && _spectropaintBucket.bucket) {
            // Export current canvas as image to bucket
            if (paintCanvas) {
                paintCanvas.toBlob(blob => {
                    if (!blob) return
                    blob.arrayBuffer().then(data => {
                        _spectropaintBucket.bucket.add({
                            source: 'spectropaint',
                            name: `spectropaint_${Date.now()}.png`,
                            imageData: data,
                            imageFormat: 'image/png',
                            imageMime: 'image/png',
                            fileType: 'image'
                        }).catch(() => {})
                    })
                }, 'image/png')
            }
            return
        }
        // Allow dropping wavecanvas to bucket (saves as WAV audio)
        const isWave = e.dataTransfer.getData('application/x-spectropaint-wav')
        if (isWave && _spectropaintBucket.bucket) {
            // Ensure audio is rendered, then add WAV to bucket
            renderAudio()
            setTimeout(() => {
                if (state.audioUrl) {
                    fetch(state.audioUrl)
                        .then(r => r.blob())
                        .then(blob => blob.arrayBuffer())
                        .then(data => {
                            _spectropaintBucket.bucket.add({
                                source: 'spectropaint',
                                name: `spectropaint_${Date.now()}.wav`,
                                wavLike: data,
                                fileType: 'wav'
                            }).catch(() => {})
                        })
                        .catch(() => {})
                }
            }, 50)
            return
        }
    },
})

// Make paint canvas draggable to bucket (exports as image)
setTimeout(() => {
    if (paintCanvas) {
        paintCanvas.draggable = true
        paintCanvas.addEventListener('dragstart', (e) => {
            e.dataTransfer.effectAllowed = 'copy'
            e.dataTransfer.setData('application/x-spectropaint-canvas', '1')
            e.dataTransfer.setData('text/plain', 'spectropaint canvas')
        })
    }
    // Make wave canvas draggable to bucket (exports as WAV audio)
    if (waveCanvas) {
        waveCanvas.draggable = true
        waveCanvas.addEventListener('dragstart', (e) => {
            e.dataTransfer.effectAllowed = 'copy'
            e.dataTransfer.setData('application/x-spectropaint-wav', '1')
            e.dataTransfer.setData('text/plain', 'spectropaint audio')
        })
    }
}, 1000)

// Allow dropping bucket images onto the paint canvas to load them
setTimeout(() => {
    if (paintCanvas && _spectropaintBucket.bucket) {
        paintCanvas.addEventListener('dragover', (e) => {
            if (e.dataTransfer.types.includes('application/x-bucket-item-id')) {
                e.preventDefault()
                e.dataTransfer.dropEffect = 'copy'
                paintCanvas.style.outline = '2px dashed var(--cf)'
            }
        })
        paintCanvas.addEventListener('dragleave', () => {
            paintCanvas.style.outline = ''
        })
        paintCanvas.addEventListener('drop', async (e) => {
            const itemId = e.dataTransfer.getData('application/x-bucket-item-id')
            if (itemId) {
                e.preventDefault()
                e.stopPropagation()
                paintCanvas.style.outline = ''
                try {
                    const dbItems = await _spectropaintBucket.dbGetAll(_spectropaintBucket.bucket.bucketId)
                    const item = dbItems.find(i => i.id === itemId)
                    if (item && item.type === 'image' && item.wav) {
                        const ext = (item.name.split('.').pop() || 'png').toLowerCase()
                        const mime = {
                            png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                            gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp'
                        }[ext] || 'image/png'
                        const blob = new Blob([item.wav], { type: mime })
                        const file = new File([blob], item.name, { type: mime })
                        loadImage(file)
                    }
                } catch (err) {
                }
            }
        }, true)
    }
}, 1500)

// Patch the download WAV button to also add WAV to bucket
setTimeout(() => {
    const dlBtn = qs('#downloadwav')
    if (dlBtn && _spectropaintBucket.bucket) {
        dlBtn.addEventListener('click', () => {
            // renderAudio() is called by the original handler, then state.audioUrl is set
            // Wait a bit for render to complete, then add to bucket
            setTimeout(() => {
                if (state.audioUrl) {
                    fetch(state.audioUrl)
                        .then(r => r.blob())
                        .then(blob => blob.arrayBuffer())
                        .then(data => {
                            _spectropaintBucket.bucket.add({
                                source: 'spectropaint',
                                name: `spectropaint_${Date.now()}.wav`,
                                wavLike: data,
                                fileType: 'wav'
                            }).catch(() => {})
                        })
                        .catch(() => {})
                }
            }, 100)
        }, true) // capture phase — runs before the original handler
    }
}, 1500)
